import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Clock, Search as SearchIcon, Users } from "lucide-react";
import { SEARCH_HINTS } from "@hub/shared";
import { Button, EmptyState, Kbd, Pill, Spinner } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { useOpenContact } from "@/app/navigation";
import { Page } from "@/app/layout/Page";
import { SearchBox } from "@/features/people/SearchBox";
import { ContactRowView } from "@/features/people/ContactRowView";
import { ContactChip } from "@/features/people/ContactChip";
import { NoteCard } from "@/features/contact/ContactSections";
import s from "../shared.module.css";

const TIPS = [
  { q: "closest friends", why: "Natural phrases map to filters" },
  { q: "company:northwind", why: "Everyone at a company" },
  { q: "lastmet<90d relationship:closer", why: "Close people you have not seen in 90 days" },
  { q: "has:phone missing:email", why: "Existence checks" },
  { q: "[twitter:* linkedin:*]", why: "Square brackets mean any of" },
  { q: "need:cfo", why: "People who need something" },
  { q: 'location:"san francisco" service:design', why: "Combine filters with free text" },
];

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const open = useOpenContact();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const results = useEngineQuery("search", { query, limit: 100 }, { enabled: query.trim().length > 0, placeholderData: (p) => p });
  const recent = useEngineQuery("getRecentSearches", undefined);
  const saveRecent = useEngineMutation("saveRecentSearch", { invalidate: ["getRecentSearches"], silent: true });
  const createGroup = useEngineMutation("createGroup", { invalidate: ["listGroups"] });
  const setQuery = (q: string) => setParams(q ? { q } : {}, { replace: true });

  useEffect(() => {
    if (!query.trim()) return;
    const t = setTimeout(() => saveRecent.mutate({ query: query.trim() }), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const r = results.data;
  const contacts = r?.contacts ?? [];
  return (
    <Page title="Search" actions={query && r ? <span className="u-small u-muted">{r.total} results</span> : undefined}>
      <SearchBox value={query} onChange={setQuery} autoFocus placeholder="Search people, groups, notes, needs and offers" showHints={false} />
      {!query.trim() && (
        <div className={s.cardGrid} style={{ marginTop: 18, gridTemplateColumns: "1fr 1fr" }}>
          <div>
            <div className="u-row" style={{ gap: 6, fontWeight: 600, marginBottom: 8 }}><Clock size={14} /> Recent</div>
            {(recent.data ?? []).length === 0 && <div className="u-small u-muted">Your recent searches show up here.</div>}
            <div className="u-col" style={{ gap: 4 }}>
              {(recent.data ?? []).map((q) => <button key={q} type="button" className={s.hint} style={{ justifyContent: "flex-start", height: 28 }} onClick={() => setQuery(q)}>{q}</button>)}
            </div>
          </div>
          <div>
            <div className="u-row" style={{ gap: 6, fontWeight: 600, marginBottom: 8 }}><SearchIcon size={14} /> Tips</div>
            <div className="u-col" style={{ gap: 6 }}>
              {TIPS.map((t) => (
                <button key={t.q} type="button" onClick={() => setQuery(t.q)} style={{ textAlign: "left", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 10px", background: "var(--bg-elevated)", cursor: "pointer", color: "inherit" }}>
                  <div className="u-mono u-small">{t.q}</div>
                  <div className="u-small u-muted">{t.why}</div>
                </button>
              ))}
            </div>
            <div style={{ marginTop: 12 }} className="u-small u-muted">
              Keywords: {SEARCH_HINTS.map((h) => h.term).join(", ")}. Operators: : = &gt; &lt; &gt;= &lt;=. Press <Kbd>/</Kbd> anywhere to search.
            </div>
          </div>
        </div>
      )}
      {query.trim() && results.isLoading && <div style={{ padding: 24, display: "flex", justifyContent: "center" }}><Spinner /></div>}
      {query.trim() && r && (
        <div style={{ marginTop: 14 }}>
          {r.total === 0 && <EmptyState title="Nothing matches">Check the chips above to see how the query was read, or try fewer terms.</EmptyState>}
          {r.groups.length > 0 && (
            <section className={s.section}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Groups</div>
              <div className="u-row" style={{ gap: 6, flexWrap: "wrap" }}>{r.groups.map((g) => <Pill key={g.id} tone="accent" className="" title={g.description ?? undefined}><span onClick={() => navigate(`/group/${g.id}`)} style={{ cursor: "pointer" }}>{g.name}</span></Pill>)}</div>
            </section>
          )}
          {contacts.length > 0 && (
            <section className={s.section}>
              <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontWeight: 600 }}>People ({contacts.length})</span>
                <span className="u-row">
                  <Button size="sm" variant="ghost" onClick={() => setSelected(selected.size === contacts.length ? new Set() : new Set(contacts.map((c) => c.contactId)))}>{selected.size === contacts.length ? "Clear" : "Select all"}</Button>
                  <Button size="sm" variant="ghost" icon={<Users size={13} />} onClick={() => createGroup.mutate({ name: query.slice(0, 40), kind: "smart", filter: query }, { onSuccess: (g) => navigate(`/group/${g.id}`) })}>Save as smart group</Button>
                  {selected.size > 0 && <Button size="sm" onClick={() => createGroup.mutate({ name: `Search: ${query.slice(0, 30)}`, contactIds: Array.from(selected) }, { onSuccess: (g) => navigate(`/group/${g.id}`) })}>Group {selected.size} selected</Button>}
                </span>
              </div>
              <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                {contacts.map((c) => <ContactRowView key={c.contactId} row={c} selected={selected.has(c.contactId)} onToggle={() => setSelected((s) => { const n = new Set(s); if (n.has(c.contactId)) n.delete(c.contactId); else n.add(c.contactId); return n; })} onOpen={() => open(c.contactId)} />)}
              </div>
            </section>
          )}
          {r.needs.length > 0 && (
            <section className={s.section}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Needs</div>
              {r.needs.map((n) => <div key={n.id} className="u-row" style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}><ContactChip contactId={n.contactId} /><span className="u-grow">{n.title}</span><Pill tone="warning">{n.category}</Pill></div>)}
            </section>
          )}
          {r.services.length > 0 && (
            <section className={s.section}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Offers</div>
              {r.services.map((sv) => <div key={sv.id} className="u-row" style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}><ContactChip contactId={sv.contactId} /><span className="u-grow">{sv.title}</span><Pill tone="info">{sv.category}</Pill></div>)}
            </section>
          )}
          {r.notes.length > 0 && (
            <section className={s.section}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Notes</div>
              {r.notes.map((n) => <NoteCard key={n.id} note={n} contactName={n.contactName} showContact />)}
            </section>
          )}
        </div>
      )}
    </Page>
  );
}
